import React, { useState, useEffect } from "react";
import Sidebar from "../../components/sidebar/Sidebar";
import Select from "react-select";
import Navbar from "../../components/navbar/Navbar";
import { useNavigate, useLocation } from "react-router-dom";
import { ciInputs } from "../../formSource";
import { serverUrl } from "../../temp";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";

const theme = createTheme({
  palette: {
    primary: {
      main: "#7451f8",
    },
    secondary: {
      main: "#6c757d",
    },
  },
});

const statusOptions = [
  { value: 1, label: "Active" },
  { value: 0, label: "Inactive" },
];

const CiUpdate = ({ title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const ciId = location.pathname.match(/\/update-ci\/(\d+)/)?.[1];
  console.log("id", location.pathname.match(/\/update-ci\/(\d+)/));
  const [inputValues, setInputValues] = useState({});
  const [userOptions, setUserOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const center_id = localStorage.getItem("center_id");
  const token = localStorage.getItem("token");

  const redirectToLogin = () => {
    alert("Please Login first then you can access this page...");
    window.location.href = "/";
  };

  // Fetch CI data
  useEffect(() => {
    const fetchCiData = async () => {
      try {
        const response = await fetch(`${serverUrl}/ci/${ciId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch CI data");
        const data = await response.json();

        if (data.data) {
          const ciData = data.data;

          // Handle class selection (multi-select)
          const classNames = ciData.class_name?.split(",") || [];
          const classIds = ciData.class_id?.split(",") || [];
          const classList = classNames
            .map((name, index) => ({
              value: classIds[index]?.trim(),
              label: name.trim(),
            }))
            .filter((cls) => cls.value && cls.label);

          // Handle user selection
          const userOption = {
            value: ciData.user_id,
            label: ciData.user_name,
          };

          // Handle status
          const statusOption =
            ciData.status == 1
              ? { value: 1, label: "Active" }
              : { value: 0, label: "Inactive" };

          setSelectedUser(userOption);
          setSelectedClasses(classList);
          setSelectedStatus(statusOption);
          setInputValues(ciData);
        }
      } catch (error) {
        console.error("Error fetching CI:", error);
        toast.error("Failed to load CI data");
      }
    };

    if (ciId) fetchCiData();
  }, [ciId, token]);

  // Fetch user options
  useEffect(() => {
    fetchUserNames();
  }, []);

  const fetchUserNames = async () => {
    try {
      const response = await fetch(`${serverUrl}/user_role_ids/${center_id}`);
      if (!response.ok) throw new Error("Failed to fetch users");
      const data = await response.json();
      const nameList = data.data.map(({ id, name }) => ({
        value: id,
        label: name,
      }));
      setUserOptions(nameList);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch user data");
    }
  };

  // Fetch class options
  useEffect(() => {
    fetchClassNames();
  }, []);

  const fetchClassNames = async () => {
    try {
      const response = await fetch(`${serverUrl}/class_ids/${center_id}`);
      if (!response.ok) throw new Error("Failed to fetch classes");
      const data = await response.json();
      const nameList = data.data.map(({ id, name }) => ({
        value: id,
        label: name,
      }));
      setClassOptions(nameList);
    } catch (error) {
      console.error("Error fetching classes:", error);
      toast.error("Failed to fetch class data");
    }
  };

  // Handlers
  const handleInputChange = (e) => {
    setInputValues({
      ...inputValues,
      [e.target.name]: e.target.value,
    });
  };

  const handleClassSelectChange = (selectedOptions) => {
    setSelectedClasses(selectedOptions);
  };

  const handleUserSelectChange = (selectedOption) => {
    setSelectedUser(selectedOption);
    setInputValues({
      ...inputValues,
      user_id: selectedOption.value,
    });
  };

  const handleStatusSelectChange = (selectedOption) => {
    setSelectedStatus(selectedOption);
    setInputValues({
      ...inputValues,
      status: selectedOption.value,
    });
  };

  // Update CI
  const handleUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const classIds = selectedClasses.map((cls) => cls.value);

    const formData = new FormData();
    formData.append("id", ciId);
    formData.append("center_id", center_id);
    formData.append("user_id", inputValues.user_id);
    formData.append("class_id", classIds);
    formData.append("status", inputValues.status || 1);

    try {
      const response = await fetch(`${serverUrl}/upd_ci/${ciId}`, {
        method: "PUT",
        body: formData,
      });

      const data = await response.json();

      if (data.code >= 400) {
        if (typeof data.message === "object") {
          Object.entries(data.message).forEach(([field, errors]) => {
            errors.forEach((errMsg) => {
              toast.error(`${field}: ${errMsg}`);
            });
          });
        } else {
          toast.error(data.message || "Something went wrong.");
        }
      } else {
        toast.success("CI updated successfully!");
        setTimeout(() => navigate("/ci"), 1000);
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      {!token && redirectToLogin()}
      {token && (
        <div className="update">
          <Sidebar />
          <div className="updateContainer">
            <Navbar />
            <div className="contentWrapper">
              <Card className="formCard">
                <CardContent className="cardContent">
                  <Typography
                    variant="h5"
                    component="h2"
                    gutterBottom
                    className="formTitle"
                  >
                    {title}
                  </Typography>
                  <form onSubmit={handleUpdate} className="responsiveForm">
                    <Grid container spacing={2}>
                      {ciInputs.map((input) => (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          key={input.id}
                          className="formGridItem"
                        >
                          <div className="formInput">
                            <label>{input.label}</label>
                            {input.fieldName === "user_id" ? (
                              <Select
                                options={userOptions}
                                value={selectedUser}
                                onChange={handleUserSelectChange}
                                required
                                className="selectInput"
                              />
                            ) : input.fieldName === "class_id" ? (
                              <Select
                                options={classOptions}
                                isMulti
                                value={selectedClasses}
                                onChange={handleClassSelectChange}
                                required
                                className="selectInput"
                              />
                            ) : input.fieldName === "status" ? (
                              <Select
                                options={statusOptions}
                                value={selectedStatus}
                                onChange={handleStatusSelectChange}
                                required
                                className="selectInput"
                              />
                            ) : (
                              <input
                                type={input.type}
                                placeholder={input.placeholder}
                                name={input.fieldName}
                                value={inputValues[input.fieldName] || ""}
                                onChange={handleInputChange}
                                required={input.fieldName !== "status"}
                              />
                            )}
                          </div>
                        </Grid>
                      ))}
                    </Grid>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 3,
                        mt: 4,
                        pb: 2,
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => navigate("/ci")}
                        sx={{ minWidth: "120px", height: "42px" }}
                        disabled={isLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{ minWidth: "150px", height: "42px" }}
                        disabled={isLoading}
                      >
                        {isLoading ? "Updating..." : "Update CI"}
                      </Button>
                    </Box>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}
      <ToastContainer position="top-right" autoClose={3000} />
    </ThemeProvider>
  );
};

export default CiUpdate;
